// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Stack, useRouter } from 'expo-router';
import { useMemo, useState } from 'react';
import { Linking, Platform, Pressable, ScrollView, StyleSheet } from 'react-native';

import { Text, View } from '@/components/Themed';
import { useUser } from '@/app/user-context';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';
import { AnalysisStepsModal } from '@/features/fleet/AnalysisStepsModal';
import { BatchQueueModal } from '@/features/fleet/BatchQueueModal';
import { buildJobLogsUrl } from '@/features/fleet/jobLogsUrl';
import {
  useBatchFleet,
  type FleetActivityItem,
  type FleetJob,
  type FleetKindStatistics,
  type FleetStatistics,
  type FleetWorker,
} from '@/features/fleet/useBatchFleet';

type Translate = (key: string, params?: Record<string, string | number>) => string;

// Assumes the goffy repo is already checked out on the machine — the install script lives in
// it, so there is nothing to curl. install.sh itself writes .env on its first run and reports
// what to fill in; this only needs to get that far.
const INSTALL_COMMAND = 'cd services/batch-worker/deploy && bash install.sh';

// install.sh seeds .env from whichever of these is already exported on a FIRST run only (see
// its own header) — so passing the AWS key inline skips the stop-edit-.env-rerun round trip
// entirely. The angle-bracket placeholders are deliberate: this is a template to fill in
// before running, not something meant to be pasted verbatim.
const INSTALL_COMMAND_WITH_ENV =
  'AWS_ACCESS_KEY_ID=<access key id> AWS_SECRET_ACCESS_KEY=<secret access key> WORKER_ID=<name> \\\n' +
  '  bash services/batch-worker/deploy/install.sh';

const README_URL = 'https://git.zyberze.ro/goffy/goffy/src/branch/main/services/batch-worker/README.md';

/**
 * The batch-worker fleet, in one place: which off-cloud servers (services/batch-worker) have
 * reported in and how recently, how deep each job queue is, and what has run lately. A
 * developer's answer to "is anything on fire" and "did my release roll out" without SSHing
 * anywhere or reading CloudWatch — everything here already exists for the per-course and
 * per-job admin panels; this just puts it all on one screen instead of one course at a time.
 *
 * Developer-or-admin gated (see BFF GeodataEndpoints.ResolveDeveloperCaller): this is
 * infrastructure detail, not course data, so it does not require the course-admin role.
 */
export default function BatchFleetScreen() {
  const { t } = useI18n();
  const { user } = useUser();
  const { isDeveloper, isAdmin } = useEffectiveRoles(user);
  const canView = isDeveloper || isAdmin;
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { state, refresh } = useBatchFleet(apiBaseUrl, user?.token, canView);
  // Which run's stages are open, if any. Held here rather than in the row so the two places
  // that render rows (the activity list and a queue tile's expansion) share one panel.
  const [stepsRunId, setStepsRunId] = useState<string | null>(null);
  const router = useRouter();

  return (
    <>
      <Stack.Screen options={{ title: t('batchFleetPage.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('batchFleetPage.title')}</Text>
        <Text style={styles.intro}>{t('batchFleetPage.intro')}</Text>
        {/* The runs below say what happened; this says what the stages are. That knowledge
            lived only in three services' source comments. */}
        <Pressable
          style={styles.helpLink}
          accessibilityRole="button"
          testID="batch-fleet-analysis-help"
          onPress={() => router.push('/analysis-help')}
        >
          <Text style={styles.helpLinkText}>{t('batchFleetPage.howAnalysisWorks')}</Text>
        </Pressable>

        {!canView ? (
          <Text style={styles.paragraph} testID="batch-fleet-forbidden">
            {t('batchFleetPage.forbidden')}
          </Text>
        ) : state.status === 'loading' ? (
          <Text style={styles.paragraph}>{t('batchFleetPage.loading')}</Text>
        ) : state.status === 'forbidden' ? (
          <Text style={styles.paragraph} testID="batch-fleet-forbidden">
            {t('batchFleetPage.forbidden')}
          </Text>
        ) : state.status === 'failed' ? (
          <>
            <Text style={styles.paragraph} testID="batch-fleet-failed">
              {t('batchFleetPage.failed')}
            </Text>
            <RefreshButton onPress={refresh} label={t('batchFleetPage.retry')} />
          </>
        ) : (
          <>
            <View style={styles.refreshRow}>
              <Text style={styles.updatedAt} testID="batch-fleet-updated-at">
                {t('batchFleetPage.updatedAt', { time: formatTime(state.loadedAt) })}
              </Text>
              <RefreshButton onPress={refresh} label={t('batchFleetPage.refresh')} />
            </View>

            <Text style={styles.heading}>{t('batchFleetPage.statisticsHeading')}</Text>
            <StatisticsRow statistics={state.statistics} t={t} />

            <Text style={styles.heading}>{t('batchFleetPage.queuesHeading')}</Text>
            <QueueRow queues={state.queues} apiBaseUrl={apiBaseUrl} token={user?.token ?? null} t={t} />

            <Text style={styles.heading}>{t('batchFleetPage.workersHeading')}</Text>
            {state.workers.length === 0 ? (
              <Text style={styles.paragraph} testID="batch-fleet-no-workers">
                {t('batchFleetPage.noWorkers')}
              </Text>
            ) : (
              state.workers.map((worker) => <WorkerCard key={worker.workerId} worker={worker} t={t} />)
            )}

            <Text style={styles.heading}>{t('batchFleetPage.addServerHeading')}</Text>
            <AddServerCard t={t} />

            <Text style={styles.heading}>{t('batchFleetPage.activityHeading')}</Text>
            {state.activity.length === 0 ? (
              <Text style={styles.paragraph} testID="batch-fleet-no-activity">
                {t('batchFleetPage.noActivity')}
              </Text>
            ) : (
              state.activity.map((item) => (
                <ActivityRow
                  key={`${item.kind}-${item.id}`}
                  item={item}
                  t={t}
                  onViewSteps={setStepsRunId}
                />
              ))
            )}
          </>
        )}
      </ScrollView>
      <AnalysisStepsModal
        apiBaseUrl={apiBaseUrl}
        token={user?.token ?? null}
        runId={stepsRunId}
        onClose={() => setStepsRunId(null)}
        t={t}
      />
    </>
  );
}

function RefreshButton({ onPress, label }: { onPress: () => void; label: string }) {
  return (
    <Pressable style={styles.refreshButton} testID="batch-fleet-refresh" onPress={onPress}>
      <Text style={styles.refreshButtonText}>{label}</Text>
    </Pressable>
  );
}

function StatisticsRow({ statistics, t }: { statistics: FleetStatistics; t: Translate }) {
  return (
    <View style={styles.queueRow}>
      <View style={styles.queueTile} testID="batch-fleet-stats-workers">
        <Text style={styles.queueTileCount}>
          {statistics.workersOnline}/{statistics.workersTotal}
        </Text>
        <Text style={styles.queueTileLabel}>{t('batchFleetPage.statsWorkersLabel')}</Text>
      </View>
      <KindStatTile testID="batch-fleet-stats-analysis" label={t('batchFleetPage.kind.course-analysis')} stats={statistics.courseAnalysis} t={t} />
      <KindStatTile testID="batch-fleet-stats-flyover" label={t('batchFleetPage.kind.flyover')} stats={statistics.flyover} t={t} />
      <KindStatTile testID="batch-fleet-stats-ml" label={t('batchFleetPage.kind.ml')} stats={statistics.ml} t={t} />
    </View>
  );
}

function KindStatTile({
  testID,
  label,
  stats,
  t,
}: {
  testID: string;
  label: string;
  stats: FleetKindStatistics;
  t: Translate;
}) {
  const rate = stats.total > 0 ? Math.round((stats.succeeded / stats.total) * 100) : null;
  return (
    <View style={styles.queueTile} testID={testID}>
      <Text style={styles.queueTileCount}>{rate === null ? '—' : `${rate}%`}</Text>
      <Text style={styles.queueTileLabel}>{label}</Text>
      <Text style={styles.statsDetail}>
        {t('batchFleetPage.statsDetail', { succeeded: stats.succeeded, failed: stats.failed, total: stats.total })}
      </Text>
    </View>
  );
}

/**
 * The three queue tiles, each a button onto the jobs behind the number. A depth on its own
 * ("3 waiting") never answers the question an operator actually has — which course is stuck,
 * and since when — so pressing a tile opens the queue itself in a window.
 *
 * It asks the queue endpoint rather than filtering the fleet payload's activity list, which
 * carries only the most recent jobs per kind: a queue twenty-four deep listed two of them and
 * had to admit it ("showing 2 of 24"), which is worse than not offering the list at all.
 */
function QueueRow({
  queues,
  apiBaseUrl,
  token,
  t,
}: {
  queues: {
    courseAnalysisDispatched: number;
    flyoverQueued: number;
    mlQueued: number;
    analysisJobsOpen: Record<string, number>;
  };
  apiBaseUrl: string;
  token: string | null;
  t: Translate;
}) {
  const [openKind, setOpenKind] = useState<FleetActivityItem['kind'] | null>(null);

  const tiles: Array<{
    testID: string;
    label: string;
    count: number;
    kind: FleetActivityItem['kind'];
  }> = [
    {
      testID: 'batch-fleet-queue-analysis',
      label: t('batchFleetPage.queueCourseAnalysis'),
      count: queues.courseAnalysisDispatched,
      kind: 'course-analysis',
    },
    {
      testID: 'batch-fleet-queue-flyover',
      label: t('batchFleetPage.queueFlyover'),
      count: queues.flyoverQueued,
      kind: 'flyover',
    },
    {
      testID: 'batch-fleet-queue-ml',
      label: t('batchFleetPage.queueMl'),
      count: queues.mlQueued,
      kind: 'ml',
    },
  ];

  const openTile = tiles.find((tile) => tile.kind === openKind);

  return (
    <>
      <View style={styles.queueRow}>
        {tiles.map((tile) => (
          <Pressable
            key={tile.testID}
            style={[
              styles.queueTile,
              tile.count > 0 && styles.queueTileBusy,
              openKind === tile.kind && styles.queueTileOpen,
            ]}
            testID={tile.testID}
            accessibilityRole="button"
            onPress={() => setOpenKind(tile.kind)}
          >
            <Text style={styles.queueTileCount}>{tile.count}</Text>
            <Text style={styles.queueTileLabel}>{tile.label}</Text>
            {/* A run is several jobs now; the kinds say what kind of worker the queue is
                waiting for — a pile of model detections needs the ML box, not another laptop. */}
            {tile.kind === 'course-analysis' && Object.keys(queues.analysisJobsOpen ?? {}).length > 0 ? (
              <Text style={styles.meta} testID="batch-fleet-queue-analysis-kinds">
                {t('batchFleetPage.queueJobsByKind', {
                  summary: Object.entries(queues.analysisJobsOpen ?? {})
                    .map(([kind, count]) => `${t(`courseAnalysis.status.jobKind.${kind}`)} ${count}`)
                    .join(' · '),
                })}
              </Text>
            ) : null}
          </Pressable>
        ))}
      </View>

      <BatchQueueModal
        apiBaseUrl={apiBaseUrl}
        token={token}
        kind={openKind}
        label={openTile?.label ?? ''}
        onClose={() => setOpenKind(null)}
        t={t}
      />
    </>
  );
}

/**
 * "Working" on its own doesn't say what — this fills in the job kind and, when the job is
 * course-scoped, the course.
 */
function busyLabel(job: FleetJob, t: Translate): string {
  // The specific job when geodata could name it ("detection · lantmateriet-orto · classical"),
  // the kind otherwise; and whether it is actually running or queued for the analyser.
  const kind = job.detail ?? t(`batchFleetPage.kind.${job.kind}`);
  const label = job.courseName
    ? t('batchFleetPage.busyDetail', { kind, course: job.courseName })
    : t('batchFleetPage.busyKindOnly', { kind });
  return job.waiting ? `${label} · ${t('batchFleetPage.busyWaiting')}` : label;
}

function WorkerCard({ worker, t }: { worker: FleetWorker; t: Translate }) {
  const capacityLine =
    worker.os && worker.architecture && worker.cpuCount !== null && worker.totalMemoryBytes !== null
      ? t('batchFleetPage.capacityLine', {
          platform: `${worker.os}/${worker.architecture}`,
          cpuCount: worker.cpuCount,
          memory: formatBytes(worker.totalMemoryBytes),
        })
      : t('batchFleetPage.capacityUnknown');

  // How long the machine has been up, which "Online" on its own does not say — a worker that
  // restarts every few minutes reads as online at every glance.
  const startedMillis = worker.startedAt ? Date.parse(worker.startedAt) : NaN;
  const startedLine = Number.isNaN(startedMillis)
    ? t('batchFleetPage.startedUnknown')
    : t('batchFleetPage.startedAt', {
        ago: formatAgo(worker.startedAt as string, t),
        time: formatTime(startedMillis),
      });

  // Whether this machine trains on its GPU — the question the fleet page could not answer
  // before, and the one that decides whether a training job takes minutes or an afternoon.
  // Only a worker with a training runner beside it has an answer: the worker container itself
  // is Linux in a VM and never has an accelerator, so a null here means "no runner to ask",
  // not "no GPU".
  const accelerator = worker.mlAccelerator;
  const usesGpu = accelerator !== null && accelerator !== 'cpu';
  // Null is "nothing answered", which is worth saying out loud on a machine that takes ML
  // jobs — its runner is down, or too old to report an accelerator — and worth staying quiet
  // about on one that does not train at all, where the question never arises. Showing no chip
  // for either was the gap: a blank card reads as "fine", and the two cases that are not fine
  // (trains on CPU, cannot say) looked exactly like the one that is.
  const trainsMl = worker.capabilities.includes('ml');
  const showAccelerator = accelerator !== null || trainsMl;

  const jobs = worker.currentJobs;

  return (
    <View style={styles.card} testID="batch-fleet-worker">
      <View style={styles.cardHeaderRow}>
        <View style={[styles.dot, worker.online ? styles.dotOnline : styles.dotOffline]} />
        <Text style={styles.cardTitle}>{worker.workerId}</Text>
        <Text style={styles.cardSubtitle}>{worker.host}</Text>
      </View>
      <Text style={styles.meta}>
        {worker.online
          ? t('batchFleetPage.online')
          : t('batchFleetPage.offlineSince', { time: formatAgo(worker.lastSeenAt, t) })}
      </Text>
      <Text style={styles.meta}>
        {t('batchFleetPage.version', { version: worker.version ?? t('batchFleetPage.versionUnknown') })}
      </Text>
      <Text style={styles.meta} testID="batch-fleet-worker-capacity">
        {capacityLine}
      </Text>
      <Text style={styles.meta} testID="batch-fleet-worker-started">
        {startedLine}
      </Text>
      {showAccelerator || worker.capabilities.length > 0 || worker.modelVersions.length > 0 ? (
        <View style={styles.tagRow}>
          {showAccelerator ? (
            // Loud when it is a GPU, quiet when it is not: a wall of neutral chips is exactly
            // where "this one is training on CPU" goes unnoticed for an afternoon.
            <View
              style={usesGpu ? [styles.badge, styles.badgeGood] : styles.tag}
              testID="batch-fleet-worker-accelerator"
            >
              <Text style={usesGpu ? styles.badgeText : styles.tagText}>
                {usesGpu
                  ? t('batchFleetPage.acceleratorGpu', { device: accelerator })
                  : accelerator === 'cpu'
                    ? t('batchFleetPage.acceleratorCpu')
                    : // Deliberately not "no GPU": the runner did not answer, so this machine
                      // may well have one. Saying "no GPU" here would send someone hunting for
                      // a GPU that is present and a runner that is simply not running.
                      t('batchFleetPage.acceleratorUnknown')}
              </Text>
            </View>
          ) : null}
          {worker.capabilities.map((capability) => (
            <View key={capability} style={styles.tag}>
              <Text style={styles.tagText}>{capability}</Text>
            </View>
          ))}
          {/* Every model on this machine is run against each course it analyses, so the count
              is what a comparison sweep costs here — and an empty model-detection worker is
              the one shape that looks capable and contributes nothing. */}
          {worker.modelVersions.length > 0 ? (
            <View style={styles.tag} testID="batch-fleet-worker-models">
              <Text style={styles.tagText}>
                {t('batchFleetPage.models', { count: worker.modelVersions.length })}
              </Text>
            </View>
          ) : null}
        </View>
      ) : null}
      {jobs.length > 0 ? (
        // One line per job in flight — a worker consumes each job kind on its own queue, so
        // training and a course analysis can run side by side, and the analysis must not hide
        // behind the training. Each line doubles as the way into that job's logs: "Course
        // analysis · Chalmers GK" is exactly the point where the next question is "and what is
        // it doing?", and the answer is a Kibana search away on an id the card already holds.
        jobs.map((job) => (
          <Pressable
            key={job.id}
            testID="batch-fleet-worker-job-logs"
            accessibilityRole="link"
            accessibilityLabel={t('batchFleetPage.jobLogsLink')}
            onPress={() => Linking.openURL(buildJobLogsUrl(job.id))}
          >
            <Text style={[styles.metaBusy, styles.metaBusyLink]}>{busyLabel(job, t)}</Text>
          </Pressable>
        ))
      ) : (
        <Text style={styles.meta}>{t('batchFleetPage.idle')}</Text>
      )}
    </View>
  );
}

function AddServerCard({ t }: { t: Translate }) {
  const [copied, setCopied] = useState(false);
  const [copiedEnv, setCopiedEnv] = useState(false);

  const copyToClipboard = (command: string, onDone: () => void) => {
    if (Platform.OS !== 'web' || typeof navigator === 'undefined' || !navigator.clipboard) {
      return;
    }
    void navigator.clipboard.writeText(command).then(onDone).catch(() => {});
  };

  return (
    <View style={styles.card} testID="batch-fleet-add-server">
      <Text style={styles.meta}>{t('batchFleetPage.addServerIntro')}</Text>
      <ScrollView horizontal style={styles.commandBox}>
        <Text selectable style={styles.commandText} testID="batch-fleet-install-command">
          {INSTALL_COMMAND}
        </Text>
      </ScrollView>
      {Platform.OS === 'web' ? (
        <View style={styles.copyRow}>
          <Pressable
            style={styles.refreshButton}
            testID="batch-fleet-copy-install"
            onPress={() => copyToClipboard(INSTALL_COMMAND, () => setCopied(true))}
          >
            <Text style={styles.refreshButtonText}>{t('batchFleetPage.copyCommand')}</Text>
          </Pressable>
          {copied ? (
            <Text style={styles.meta} testID="batch-fleet-copied">
              {t('batchFleetPage.copied')}
            </Text>
          ) : null}
        </View>
      ) : null}
      <Text style={styles.meta}>{t('batchFleetPage.addServerStep2')}</Text>

      {/* install.sh reads AWS_ACCESS_KEY_ID/AWS_SECRET_ACCESS_KEY/WORKER_ID (and a few more)
          from the environment on a first run only, and skips straight past the stop-and-edit
          step if they're already there — so this template is worth showing as its own thing,
          not just prose about a flag nobody would otherwise guess exists. */}
      <Text style={styles.meta}>{t('batchFleetPage.addServerEnvHeading')}</Text>
      <ScrollView horizontal style={styles.commandBox}>
        <Text selectable style={styles.commandText} testID="batch-fleet-install-command-env">
          {INSTALL_COMMAND_WITH_ENV}
        </Text>
      </ScrollView>
      {Platform.OS === 'web' ? (
        <View style={styles.copyRow}>
          <Pressable
            style={styles.refreshButton}
            testID="batch-fleet-copy-install-env"
            onPress={() => copyToClipboard(INSTALL_COMMAND_WITH_ENV, () => setCopiedEnv(true))}
          >
            <Text style={styles.refreshButtonText}>{t('batchFleetPage.copyCommand')}</Text>
          </Pressable>
          {copiedEnv ? (
            <Text style={styles.meta} testID="batch-fleet-copied-env">
              {t('batchFleetPage.copied')}
            </Text>
          ) : null}
        </View>
      ) : null}

      <Pressable testID="batch-fleet-docs-link" onPress={() => Linking.openURL(README_URL)}>
        <Text style={styles.linkText}>{t('batchFleetPage.fullDocsLink')}</Text>
      </Pressable>
    </View>
  );
}

const ACTIVITY_TONE: Record<string, 'good' | 'warn' | 'bad'> = {
  succeeded: 'good',
  queued: 'warn',
  dispatched: 'warn',
  running: 'warn',
  partial: 'warn',
  failed: 'bad',
};

function ActivityRow({
  item,
  t,
  onViewSteps,
}: {
  item: FleetActivityItem;
  t: Translate;
  /** Course analyses only: the other job kinds record no stages. Opens the job's run. */
  onViewSteps?: (runId: string) => void;
}) {
  const tone = ACTIVITY_TONE[item.status] ?? 'warn';
  const canViewSteps = item.kind === 'course-analysis' && onViewSteps !== undefined;
  return (
    <View style={styles.card} testID="batch-fleet-activity-row">
      <View style={styles.cardHeaderRow}>
        <View style={[styles.badge, toneStyle(tone)]}>
          <Text style={styles.badgeText}>{item.status}</Text>
        </View>
        <Text style={styles.cardTitle}>{t(`batchFleetPage.kind.${item.kind}`)}</Text>
        <Text style={styles.cardSubtitle}>{item.summary ?? ''}</Text>
      </View>
      {item.detail ? (
        <Text style={styles.meta} testID="batch-fleet-activity-detail">
          {item.detail}
        </Text>
      ) : null}
      <Text style={styles.meta}>{formatTime(Date.parse(item.at))}</Text>
      {item.workerId ? (
        <Text style={styles.meta}>{t('batchFleetPage.byWorker', { id: item.workerId })}</Text>
      ) : null}
      {item.error ? (
        <Text style={styles.errorText} testID="batch-fleet-activity-error">
          {item.error}
        </Text>
      ) : null}
      {canViewSteps ? (
        // The row says a run succeeded and when; this is the way to what it actually did —
        // where the time went, and what each stage moved. Offered on failures too: a run that
        // died part-way still recorded the stages it finished.
        <Pressable
          accessibilityRole="button"
          testID="batch-fleet-activity-steps"
          style={styles.stepsButton}
          onPress={() => onViewSteps?.(item.runId ?? item.id)}
        >
          <Text style={styles.stepsButtonText}>{t('batchFleetPage.viewSteps')}</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

const toneStyle = (tone: 'good' | 'warn' | 'bad') =>
  tone === 'good' ? styles.badgeGood : tone === 'warn' ? styles.badgeWarn : styles.badgeBad;

const formatTime = (millis: number): string => {
  const parsed = new Date(millis);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleString();
};

const formatAgo = (iso: string, t: Translate): string => {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const seconds = Math.max(0, Math.round((Date.now() - parsed) / 1000));
  if (seconds < 60) return t('batchFleetPage.secondsAgo', { count: seconds });
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t('batchFleetPage.minutesAgo', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('batchFleetPage.hoursAgo', { count: hours });
  const days = Math.round(hours / 24);
  return t('batchFleetPage.daysAgo', { count: days });
};

/** Binary units (GiB shown as "GB", matching how docker/compose memory limits are usually spoken of). */
const formatBytes = (bytes: number): string => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const decimals = unitIndex === 0 ? 0 : value < 10 ? 1 : 0;
  return `${value.toFixed(decimals)} ${units[unitIndex]}`;
};

const styles = StyleSheet.create({
  container: { padding: 20, gap: 4 },
  title: { fontSize: 26, fontWeight: '700', color: appColors.text.primary },
  intro: { color: appColors.text.secondary, fontSize: 13, marginBottom: 12, lineHeight: 18 },
  paragraph: { color: appColors.text.secondary, marginBottom: 12 },
  heading: { color: appColors.text.primary, fontWeight: '700', fontSize: 16, marginTop: 18, marginBottom: 8 },
  refreshRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  updatedAt: { color: appColors.text.muted, fontSize: 12 },
  refreshButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    alignSelf: 'flex-start',
  },
  refreshButtonText: { color: appColors.accent.primary, fontWeight: '700', fontSize: 12 },
  stepsButton: {
    marginTop: 8,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    alignSelf: 'flex-start',
  },
  stepsButtonText: { color: appColors.text.secondary, fontWeight: '600', fontSize: 12 },
  helpLink: { alignSelf: 'flex-start', marginBottom: 12 },
  helpLinkText: { color: appColors.accent.primary, fontSize: 13, fontWeight: '600' },
  queueRow: { flexDirection: 'row', gap: 10, flexWrap: 'wrap', marginTop: 8 },
  queueTile: {
    flexGrow: 1,
    minWidth: 120,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    paddingVertical: 12,
    paddingHorizontal: 14,
    alignItems: 'center',
  },
  queueTileBusy: { borderColor: '#6B5A1E' },
  queueTileOpen: { borderColor: appColors.text.secondary },
  queueDetail: { marginTop: 10 },
  queueTileCount: { color: appColors.text.primary, fontSize: 22, fontWeight: '800' },
  queueTileLabel: { color: appColors.text.secondary, fontSize: 11, marginTop: 2, textAlign: 'center' },
  statsDetail: { color: appColors.text.muted, fontSize: 10, marginTop: 4, textAlign: 'center' },
  card: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    padding: 12,
    marginBottom: 10,
    gap: 4,
  },
  cardHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  cardTitle: { color: appColors.text.primary, fontWeight: '700', fontSize: 14 },
  cardSubtitle: { color: appColors.text.secondary, fontSize: 12 },
  dot: { width: 9, height: 9, borderRadius: 5 },
  dotOnline: { backgroundColor: '#3FA65A' },
  dotOffline: { backgroundColor: '#8A8A78' },
  meta: { color: appColors.text.secondary, fontSize: 12 },
  metaBusy: { color: '#D7B65C', fontSize: 12, fontWeight: '600' },
  metaBusyLink: { textDecorationLine: 'underline' },
  errorText: { color: '#E08A8A', fontSize: 12 },
  tagRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap', marginTop: 2 },
  tag: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
    backgroundColor: appColors.background.app,
    borderWidth: 1,
    borderColor: appColors.border.default,
  },
  tagText: { color: appColors.text.secondary, fontSize: 10 },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 6 },
  badgeGood: { backgroundColor: '#1F5B2E' },
  badgeWarn: { backgroundColor: '#6B5A1E' },
  badgeBad: { backgroundColor: '#7A2E2E' },
  badgeText: { color: appColors.text.primary, fontSize: 10, fontWeight: '800', textTransform: 'uppercase' },
  commandBox: {
    marginTop: 8,
    borderRadius: 8,
    backgroundColor: '#03170A',
    borderWidth: 1,
    borderColor: appColors.border.default,
    padding: 10,
  },
  commandText: {
    color: '#A1D493',
    fontSize: 12,
    fontFamily: Platform.select({ web: 'monospace', ios: 'Menlo', default: 'monospace' }),
  },
  copyRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 8 },
  linkText: { color: appColors.accent.primary, textDecorationLine: 'underline', marginTop: 6, fontSize: 12 },
});
